import { useState, useRef, useEffect } from 'react';
import { Bell, AlertTriangle, Info, AlertCircle } from 'lucide-react';
import type { ActionItem } from '@maker-toolchest/shared';

interface AlertDropdownProps {
  actionItems: ActionItem[];
  isLoading?: boolean;
  error?: Error | null;
}

export function AlertDropdown({ actionItems, isLoading, error }: AlertDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const hasAlerts = actionItems.length > 0;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          relative flex h-8 w-8 items-center justify-center rounded
          transition-colors
          ${isOpen ? 'bg-background-elevated text-primary' : 'text-foreground-muted hover:bg-background-elevated hover:text-foreground'}
        `}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {hasAlerts && (
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-amber-500" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 rounded border border-border bg-background shadow-lg">
          <div className="border-b border-border p-3">
            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {error ? (
              <div className="p-6 text-center">
                <AlertCircle className="mx-auto mb-2 h-8 w-8 text-red-500" />
                <p className="text-sm text-foreground">Failed to load alerts</p>
                <p className="text-xs text-foreground-muted mt-1">Please try again later</p>
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : actionItems.length === 0 ? (
              <div className="p-6 text-center">
                <Info className="mx-auto mb-2 h-8 w-8 text-foreground-muted" />
                <p className="text-sm text-foreground-muted">No notifications</p>
                <p className="text-xs text-foreground-subtle">You're all caught up!</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {actionItems.map((item) => (
                  <AlertItem key={item.id} item={item} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface AlertItemProps {
  item: ActionItem;
}

function AlertItem({ item }: AlertItemProps) {
  const severityConfig = {
    info: {
      icon: <Info className="h-4 w-4" />,
      iconColor: 'text-blue-500',
    },
    warning: {
      icon: <AlertTriangle className="h-4 w-4" />,
      iconColor: 'text-amber-500',
    },
    error: {
      icon: <AlertCircle className="h-4 w-4" />,
      iconColor: 'text-red-500',
    },
  };

  const config = severityConfig[item.severity];

  return (
    <div className="p-3 transition-colors hover:bg-background-elevated">
      <div className="flex items-start gap-2">
        <div className={`flex-shrink-0 ${config.iconColor}`}>{config.icon}</div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{item.title}</p>
          <p className="text-xs text-foreground-muted">{item.description}</p>
        </div>
      </div>
    </div>
  );
}
